import { Theme } from '@logto/schemas';
import { matchPlatformLanguage } from './languages';

export type Appearance = 'light' | 'dark' | 'system';
export const preferenceKeys = { theme: 'authstudio:appearance', language: 'authstudio:language' };
const eventName = 'authstudio:preferences';
const transient = new Map<string, string>();
export function readPreference(key: string) {
  try {
    return transient.get(key) ?? localStorage.getItem(key);
  } catch {
    return transient.get(key) ?? null;
  }
}
export function savePreference(key: string, value: string) {
  transient.set(key, value);
  try {
    localStorage.setItem(key, value);
    transient.delete(key);
  } catch {
    /* Keep the selection for this page when storage is unavailable. */
  }
  window.dispatchEvent(new Event(eventName));
}
export function getAppearance(): Appearance {
  const value = readPreference(preferenceKeys.theme);
  return value === 'light' || value === 'dark' ? value : 'system';
}
export function getPlatformTheme(): Theme {
  const value = getAppearance();
  return value === 'dark' ||
    (value === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches)
    ? Theme.Dark
    : Theme.Light;
}
export function getPlatformLanguage(hint?: string): string {
  return (
    matchPlatformLanguage(hint)?.tag ??
    matchPlatformLanguage(readPreference(preferenceKeys.language))?.tag ??
    matchPlatformLanguage(navigator.languages?.join(' ') || navigator.language)?.tag ??
    'en'
  );
}
export function requestedLanguage(): string | undefined {
  const query = new URLSearchParams(window.location.search).get('ui_locales');
  if (query) return query;
  try {
    return sessionStorage.getItem('ui_locales') ?? undefined;
  } catch {
    return undefined;
  }
}
export function subscribePreferences(listener: () => void) {
  const media = window.matchMedia('(prefers-color-scheme: dark)');
  const onStorage = (event: StorageEvent) => {
    if (!event.key || Object.values(preferenceKeys).includes(event.key)) listener();
  };
  media.addEventListener('change', listener);
  window.addEventListener(eventName, listener);
  window.addEventListener('storage', onStorage);
  return () => {
    media.removeEventListener('change', listener);
    window.removeEventListener(eventName, listener);
    window.removeEventListener('storage', onStorage);
  };
}
